import { useMemo, useState, type ReactElement } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import { paginateRows } from '../../domain/contracts';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { EntityActivitySection } from '../activity/EntityActivitySection';
import { FilterTiles } from './FilterTiles';
import { MasterRecordPanel } from './MasterRecordPanel';
import { MasterSearchField } from './searchField';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface PartyRow {
  id: string;
  legalName: string;
  kind: string;
  roles: string[];
}

const COLUMNS: GroupedColumnDef[] = [
  { id: 'name', label: 'Name', header: 'Name' },
  { id: 'kind', label: 'Kind', header: 'Kind' },
  { id: 'roles', label: 'Roles', header: 'Roles' }
];

function groupValue(row: PartyRow, columnId: string): string {
  switch (columnId) {
    case 'name':
      return row.legalName;
    case 'kind':
      return row.kind;
    case 'roles':
      return row.roles.join(', ') || '—';
    default:
      return '—';
  }
}

export function PartyDetail({
  item,
  variant = 'panel',
  onClose,
  onBack,
  authFetch
}: {
  item: PartyRow;
  variant?: 'panel' | 'page';
  onClose?: () => void;
  onBack?: () => void;
  authFetch?: AuthFetch;
}): ReactElement {
  return (
    <MasterRecordPanel
      testId="party-side-panel"
      ariaLabel="Party details"
      title={item.legalName}
      status={item.kind}
      variant={variant}
      onClose={onClose}
      onBack={onBack}
    >
      <dl className="detail-list">
        <dt>Legal name</dt>
        <dd>{item.legalName}</dd>
        <dt>Kind</dt>
        <dd>
          <span className="status-pill">{item.kind}</span>
        </dd>
        <dt>Roles</dt>
        <dd>{item.roles.join(', ') || '—'}</dd>
      </dl>
      {authFetch && (
        <EntityActivitySection authFetch={authFetch} path={`/parties/${item.id}/activity`} testId="party-activity" />
      )}
    </MasterRecordPanel>
  );
}

export function PartiesRegister({
  rows,
  query,
  onQuery,
  onOpen,
  authFetch
}: {
  rows: PartyRow[];
  query: string;
  onQuery: (value: string) => void;
  onOpen?: (id: string) => void;
  authFetch?: AuthFetch;
}): ReactElement {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);

  const kinds = useMemo(() => [...new Set(rows.map((r) => r.kind).filter(Boolean))], [rows]);
  const filtered = useMemo(() => (filter === 'all' ? rows : rows.filter((r) => r.kind === filter)), [filter, rows]);
  const pageRows = useMemo(() => paginateRows(filtered, page, pageSize), [filtered, page, pageSize]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <>
      <FilterTiles
        testId="party-tiles"
        value={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
          setSelectedId(null);
        }}
        options={[
          { id: 'all', label: 'All', count: rows.length },
          ...kinds.map((kind) => ({
            id: kind,
            label: kind,
            count: rows.filter((r) => r.kind === kind).length
          }))
        ]}
      />
      <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
        <section className="card requests-grid-card" data-testid="parties-grid">
          <div className="row spread requests-grid-toolbar">
            <h2>Parties</h2>
          </div>
          <p className="hint">Click a row for the side panel; double-click to open the full page.</p>
          <MasterSearchField value={query} onChange={onQuery} />
          <GroupedDataGrid<PartyRow>
            columns={COLUMNS}
            rows={pageRows}
            getRowId={(r) => r.id}
            getGroupValue={groupValue}
            colSpan={3}
            emptyMessage="No parties match this status."
            groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
            renderRow={(row) => (
              <tr
                className={selectedId === row.id ? 'selected' : undefined}
                data-testid="party-row"
                onClick={() => setSelectedId(row.id)}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  onOpen?.(row.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(row.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <td>{row.legalName}</td>
                <td>
                  <span className="status-pill">{row.kind}</span>
                </td>
                <td>{row.roles.join(', ') || '—'}</td>
              </tr>
            )}
          />
          <GridPaginationBar
            idPrefix="parties"
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </section>
        {selected && <PartyDetail item={selected} authFetch={authFetch} onClose={() => setSelectedId(null)} />}
      </div>
    </>
  );
}
